// ─── World Map Generator ──────────────────────────────────────────────────────

import {
  TILE_SIZE,
  WORLD_COLS,
  WORLD_ROWS,
  TileType,
  DEPARTMENT_COLORS,
  type RoomData,
  type Interactable,
} from "./constants";
import type { DepartmentState } from "@/stores/game-store";
import { WORLD_PROPS } from "@/data/world-props";
import { ROUTE1_MAP, type MapExit } from "@/data/maps/route1";
import { TOWN_CENTER_MAP } from "@/data/maps/town-center";
import { BEACH_CAVE_MAP } from "@/data/maps/beach-cave";
import { VOLCANO_PEAK_MAP } from "@/data/maps/volcano-peak";
import { CITY_DISTRICT_MAP } from "@/data/maps/city-district";
import { CORP_TOWER_MAP } from "@/data/maps/corp-tower";

// ─── Map registry ─────────────────────────────────────────────────────────────
// Canonical id union for all maps reachable via the registry / transitions.
export type MapId = "overworld" | "route_1" | "town_center" | "beach_cave" | "volcano_peak" | "city_district" | "corp_tower";

/**
 * Map-meta sidecar: RoomData has no native fields for exits, so the registry
 * stores them here keyed by mapId. `checkExits` reads from this table.
 */
const MAP_EXITS: Record<MapId, MapExit[]> = {
  overworld: [],
  route_1: [],
  town_center: [],
  beach_cave: [],
  volcano_peak: VOLCANO_PEAK_MAP.exits ?? [],
  city_district: CITY_DISTRICT_MAP.exits ?? [],
  corp_tower: CORP_TOWER_MAP.exits ?? [],
};

/** Cache of adapted RoomData per static map. Built lazily on first request. */
const STATIC_ROOM_CACHE: Partial<Record<MapId, RoomData>> = {};

/** Holder for the runtime-generated overworld room (set by RoomManager). */
let OVERWORLD_ROOM: RoomData | null = null;

/**
 * Register the procedurally-generated overworld room with the registry so
 * `loadMap("overworld")` can return it alongside the static maps.
 */
export function registerOverworld(room: RoomData): void {
  OVERWORLD_ROOM = room;
  MAP_EXITS.overworld = [];
}

// ─── Adapters: static map literals → canonical RoomData ───────────────────────
// The three new maps in src/data/maps/* each have a slightly different shape.
// These adapters bridge them into the runtime RoomData contract used by
// renderer, collision, and the rest of the engine.

/**
 * Route 1 already extends RoomData (via RouteMap). Just project the base
 * fields and stash exits in the sidecar.
 */
function adaptRoute1(): RoomData {
  MAP_EXITS.route_1 = ROUTE1_MAP.exits.map((e) => ({ ...e }));
  return {
    id: ROUTE1_MAP.id,
    name: ROUTE1_MAP.name,
    type: ROUTE1_MAP.type,
    tileMap: ROUTE1_MAP.tileMap,
    collisionMap: ROUTE1_MAP.collisionMap,
    interactables: ROUTE1_MAP.interactables,
    width: ROUTE1_MAP.width,
    height: ROUTE1_MAP.height,
  };
}

/**
 * Town Center uses a flat `tiles: number[][]` plus typed building/npc lists.
 * Build collisionMap from tile codes (BuildingExterior + BuildingFloor + Wall +
 * PalmTrunk + TikiBar are impassable; Door/Sand/Grass are walkable). NPCs and
 * building entries become section-typed interactables for the existing engine.
 */
function adaptTownCenter(): RoomData {
  const src = TOWN_CENTER_MAP;
  const tileMap: number[][] = src.tiles.map((row) => row.slice());
  const collisionMap: boolean[][] = src.tiles.map((row) =>
    row.map((t) => isImpassableTile(t)),
  );

  const interactables: Interactable[] = [];

  for (const npc of src.npcs) {
    interactables.push({
      id: npc.id,
      type: "section",
      position: { x: npc.x * TILE_SIZE, y: npc.y * TILE_SIZE },
      width: TILE_SIZE,
      height: TILE_SIZE,
      data: { kind: "npc", facing: npc.facing, dialog: [...npc.dialog] },
    });
  }

  for (const b of src.buildings) {
    interactables.push({
      id: b.id,
      type: "building",
      position: { x: b.entryX * TILE_SIZE, y: b.entryY * TILE_SIZE },
      width: TILE_SIZE,
      height: TILE_SIZE,
      data: { action: b.entryAction },
    });
  }

  MAP_EXITS.town_center = src.exits.map((e) => ({
    x: e.x,
    y: e.y,
    targetMapId: e.targetMapId,
    targetX: e.targetX,
    targetY: e.targetY,
  }));

  return {
    id: src.id,
    name: src.name,
    type: "overworld",
    tileMap,
    collisionMap,
    interactables,
    width: src.width,
    height: src.height,
  };
}

/**
 * Beach Cave uses a tiny custom tile dictionary (0=W,1=F,2=E,3=T). Map those
 * to the engine's TileType enum so the renderer has familiar codes, and treat
 * walls as the only impassable tile.
 */
function adaptBeachCave(): RoomData {
  const src = BEACH_CAVE_MAP;
  const tileMap: number[][] = src.tiles.map((row) =>
    row.map((t) => beachCaveTileToEngineTile(t)),
  );
  const collisionMap: boolean[][] = src.tiles.map((row) =>
    row.map((t) => t === 0 /* W */),
  );

  const interactables: Interactable[] = src.pickups.map((p) => ({
    id: p.itemId,
    type: "gem",
    position: { x: p.x * TILE_SIZE, y: p.y * TILE_SIZE },
    width: TILE_SIZE,
    height: TILE_SIZE,
    data: { kind: "pickup", consumed: p.consumed },
  }));

  MAP_EXITS.beach_cave = src.exits.map((e) => ({
    x: e.x,
    y: e.y,
    targetMapId: e.targetMapId,
    targetX: e.targetX,
    targetY: e.targetY,
  }));

  return {
    id: src.id,
    name: src.name,
    type: "overworld",
    tileMap,
    collisionMap,
    interactables,
    width: src.width,
    height: src.height,
  };
}

/** Tile codes that block walking in town-center's tile vocabulary. */
function isImpassableTile(t: number): boolean {
  return (
    t === TileType.BuildingFloor ||
    t === TileType.BuildingExterior ||
    t === TileType.Wall ||
    t === TileType.PalmTrunk ||
    t === TileType.PalmLeaves ||
    t === TileType.TikiBar ||
    t === TileType.TreeCanopy ||
    t === TileType.Rock
  );
}

/** Translate beach-cave's mini-vocab (0..3) to engine TileType numbers. */
function beachCaveTileToEngineTile(t: number): number {
  switch (t) {
    case 0:
      return TileType.Wall;
    case 1:
      return TileType.BuildingFloor;
    case 2:
      return TileType.Door;
    case 3:
      return TileType.TikiBar; // reused as treasure-pickup tile
    default:
      return TileType.Empty;
  }
}


/**
 * Volcano Peak follows the same RouteMap shape as Route 1.
 */
function adaptVolcanoPeak(): RoomData {
  MAP_EXITS.volcano_peak = VOLCANO_PEAK_MAP.exits.map((e) => ({ ...e }));
  return {
    id: VOLCANO_PEAK_MAP.id,
    name: VOLCANO_PEAK_MAP.name,
    type: VOLCANO_PEAK_MAP.type,
    tileMap: VOLCANO_PEAK_MAP.tileMap,
    collisionMap: VOLCANO_PEAK_MAP.collisionMap,
    interactables: VOLCANO_PEAK_MAP.interactables,
    width: VOLCANO_PEAK_MAP.width,
    height: VOLCANO_PEAK_MAP.height,
  };
}

/**
 * City District follows the same RouteMap shape as Route 1.
 */
function adaptCityDistrict(): RoomData {
  MAP_EXITS.city_district = CITY_DISTRICT_MAP.exits.map((e) => ({ ...e }));
  return {
    id: CITY_DISTRICT_MAP.id,
    name: CITY_DISTRICT_MAP.name,
    type: CITY_DISTRICT_MAP.type,
    tileMap: CITY_DISTRICT_MAP.tileMap,
    collisionMap: CITY_DISTRICT_MAP.collisionMap,
    interactables: CITY_DISTRICT_MAP.interactables,
    width: CITY_DISTRICT_MAP.width,
    height: CITY_DISTRICT_MAP.height,
  };
}

/**
 * Corp Tower follows the same RouteMap shape as Route 1.
 */
function adaptCorpTower(): RoomData {
  MAP_EXITS.corp_tower = CORP_TOWER_MAP.exits.map((e) => ({ ...e }));
  return {
    id: CORP_TOWER_MAP.id,
    name: CORP_TOWER_MAP.name,
    type: CORP_TOWER_MAP.type,
    tileMap: CORP_TOWER_MAP.tileMap,
    collisionMap: CORP_TOWER_MAP.collisionMap,
    interactables: CORP_TOWER_MAP.interactables,
    width: CORP_TOWER_MAP.width,
    height: CORP_TOWER_MAP.height,
  };
}

/**
 * Load a map by id. Returns the canonical RoomData, or null if the id is
 * unknown or the overworld hasn't been registered yet.
 */
export function loadMap(mapId: string): RoomData | null {
  switch (mapId) {
    case "overworld":
      return OVERWORLD_ROOM;
    case "route_1": {
      const cached = STATIC_ROOM_CACHE.route_1;
      if (cached) return cached;
      const room = adaptRoute1();
      STATIC_ROOM_CACHE.route_1 = room;
      return room;
    }
    case "town_center": {
      const cached = STATIC_ROOM_CACHE.town_center;
      if (cached) return cached;
      const room = adaptTownCenter();
      STATIC_ROOM_CACHE.town_center = room;
      return room;
    }
    case "beach_cave": {
      const cached = STATIC_ROOM_CACHE.beach_cave;
      if (cached) return cached;
      const room = adaptBeachCave();
      STATIC_ROOM_CACHE.beach_cave = room;
      return room;
    }
    case "volcano_peak": {
      const cached = STATIC_ROOM_CACHE.volcano_peak;
      if (cached) return cached;
      const room = adaptVolcanoPeak();
      STATIC_ROOM_CACHE.volcano_peak = room;
      return room;
    }
    case "city_district": {
      const cached = STATIC_ROOM_CACHE.city_district;
      if (cached) return cached;
      const room = adaptCityDistrict();
      STATIC_ROOM_CACHE.city_district = room;
      return room;
    }
    case "corp_tower": {
      const cached = STATIC_ROOM_CACHE.corp_tower;
      if (cached) return cached;
      const room = adaptCorpTower();
      STATIC_ROOM_CACHE.corp_tower = room;
      return room;
    }
    default:
      return null;
  }
}

/** Return the exits registered for a given map id (empty if unknown). */
export function getMapExits(mapId: string): readonly MapExit[] {
  if (mapId in MAP_EXITS) {
    return MAP_EXITS[mapId as MapId];
  }
  return [];
}

/**
 * Returns the exit that matches the player's tile-space position on the given
 * map, or null if the player isn't standing on any exit. `playerX`/`playerY`
 * are world pixels — converted internally to tile coords.
 */
export function checkExits(
  playerX: number,
  playerY: number,
  currentMapId: string,
): MapExit | null {
  const col = Math.floor(playerX / TILE_SIZE);
  const row = Math.floor(playerY / TILE_SIZE);
  const exits = getMapExits(currentMapId);
  for (const exit of exits) {
    if (exit.x === col && exit.y === row) {
      return exit;
    }
  }
  return null;
}

// ─── Building plot definitions ────────────────────────────────────────────────

interface BuildingPlot {
  col: number;
  row: number;
  width: number;  // in tiles
  height: number; // in tiles
}

// Predefined building plot positions along the boardwalk
const BUILDING_PLOTS: BuildingPlot[] = [
  { col: 5, row: 8, width: 10, height: 8 },
  { col: 20, row: 8, width: 10, height: 8 },
  { col: 35, row: 8, width: 10, height: 8 },
  { col: 50, row: 8, width: 10, height: 8 },
  { col: 65, row: 8, width: 10, height: 8 },
  { col: 5, row: 22, width: 10, height: 8 },
  { col: 20, row: 22, width: 10, height: 8 },
  { col: 35, row: 22, width: 10, height: 8 },
  { col: 50, row: 22, width: 10, height: 8 },
  { col: 65, row: 22, width: 10, height: 8 },
];

// Palm tree positions
const PALM_POSITIONS = [
  { col: 3, row: 35 },
  { col: 18, row: 36 },
  { col: 33, row: 35 },
  { col: 48, row: 36 },
  { col: 63, row: 35 },
  { col: 10, row: 36 },
  { col: 25, row: 35 },
  { col: 40, row: 36 },
  { col: 55, row: 35 },
  { col: 70, row: 36 },
];

// Pool area
const POOL_AREA = { col: 30, row: 38, width: 12, height: 5 };

// Tiki bar
const TIKI_BAR = { col: 55, row: 38, width: 6, height: 3 };

export class WorldMap {
  private tileMap: number[][] = [];
  private collisionMap: boolean[][] = [];
  private interactables: Interactable[] = [];
  private buildings: Map<string, BuildingPlot & { deptId: string }> = new Map();

  /**
   * Generate the Miami Beach overworld map.
   *
   * @param departments - Player's departments to place as buildings
   * @returns RoomData for the overworld
   */
  generate(departments: DepartmentState[]): RoomData {
    // Initialize maps
    this.tileMap = [];
    this.collisionMap = [];
    this.interactables = [];
    this.buildings.clear();

    for (let row = 0; row < WORLD_ROWS; row++) {
      this.tileMap[row] = [];
      this.collisionMap[row] = [];
      for (let col = 0; col < WORLD_COLS; col++) {
        this.tileMap[row][col] = TileType.Empty;
        this.collisionMap[row][col] = false;
      }
    }

    // Layer 1: Grass base (top area)
    this.fillRect(0, 0, WORLD_COLS, 34, TileType.Grass, false);

    // Layer 2: Boardwalk (horizontal path)
    this.fillRect(0, 34, WORLD_COLS, 3, TileType.Boardwalk, false);

    // Layer 3: Sand beach
    this.fillRect(0, 37, WORLD_COLS, 14, TileType.Sand, false);

    // Layer 4: Ocean at the very bottom
    this.fillRect(0, 51, WORLD_COLS, 5, TileType.Ocean, true);

    // Layer 5: Animated wave line
    this.fillRect(0, 51, WORLD_COLS, 1, TileType.WaveAnimated, true);

    // Place buildings for departments
    departments.forEach((dept, i) => {
      if (i < BUILDING_PLOTS.length) {
        this.placeBuilding(dept, BUILDING_PLOTS[i]);
      }
    });

    // Place palm trees
    for (const palm of PALM_POSITIONS) {
      if (palm.row < WORLD_ROWS && palm.col < WORLD_COLS) {
        this.tileMap[palm.row][palm.col] = TileType.PalmTrunk;
        this.collisionMap[palm.row][palm.col] = true;
        if (palm.row - 1 >= 0) {
          this.tileMap[palm.row - 1][palm.col] = TileType.PalmLeaves;
          if (palm.col - 1 >= 0) {
            this.tileMap[palm.row - 1][palm.col - 1] = TileType.PalmLeaves;
          }
          if (palm.col + 1 < WORLD_COLS) {
            this.tileMap[palm.row - 1][palm.col + 1] = TileType.PalmLeaves;
          }
        }
      }
    }

    // Place pool
    this.fillRect(
      POOL_AREA.col,
      POOL_AREA.row,
      POOL_AREA.width,
      POOL_AREA.height,
      TileType.Pool,
      true,
    );

    // Place tiki bar
    this.fillRect(
      TIKI_BAR.col,
      TIKI_BAR.row,
      TIKI_BAR.width,
      TIKI_BAR.height,
      TileType.TikiBar,
      true,
    );

    // ── Terrain variety: paths, trees, rocks, flowers, dark grass ────────────

    // Building columns and their path columns (2-wide vertical paths)
    const buildingCols = [5, 20, 35, 50, 65];

    // Vertical stone paths through grass area
    for (const baseCol of buildingCols) {
      const pathCol = baseCol + 4; // centre of 10-wide plot
      for (let row = 0; row < 34; row++) {
        for (let pc = pathCol; pc <= pathCol + 1; pc++) {
          if (pc >= 0 && pc < WORLD_COLS) {
            const cur = this.tileMap[row][pc];
            if (cur === TileType.Grass || cur === TileType.DarkGrass || cur === TileType.FlowerPatch) {
              this.tileMap[row][pc] = TileType.Path;
              this.collisionMap[row][pc] = false;
            }
          }
        }
      }
    }

    // Horizontal main street at row 16
    for (let col = 0; col < WORLD_COLS; col++) {
      for (let pr = 16; pr <= 17; pr++) {
        const cur = this.tileMap[pr][col];
        if (cur === TileType.Grass || cur === TileType.DarkGrass || cur === TileType.FlowerPatch) {
          this.tileMap[pr][col] = TileType.Path;
          this.collisionMap[pr][col] = false;
        }
      }
    }

    // Small plazas where the vertical paths cross the main street
    for (const baseCol of buildingCols) {
      const pathCol = baseCol + 4;
      this.fillPathPlaza(pathCol - 1, 15, 4, 4);
    }

    // Door-front plazas for the lower building row (doors exit onto row 30)
    for (const baseCol of buildingCols) {
      const doorCol = baseCol + 5;
      this.fillPathPlaza(doorCol - 1, 30, 4, 2);
    }

    // Pond: organic water blob with a walkable sand ring, northern clearing
    this.placePond(31.5, 4, 4.5, 2.5);

    // Helper: check if tile is inside any building plot or palm area
    const isInBuilding = (col: number, row: number): boolean => {
      for (const plot of BUILDING_PLOTS) {
        if (
          col >= plot.col - 1 && col <= plot.col + plot.width &&
          row >= plot.row - 1 && row <= plot.row + plot.height
        ) return true;
      }
      return false;
    };

    // Tall-grass encounter patches: organic, jitter-edged blobs (walkable).
    // Trees/rocks are now decorative props (see src/data/world-props.ts), so
    // the tile layer only carries terrain.
    const GRASS_BLOBS: Array<[number, number, number, number]> = [
      [15, 3, 4.5, 2.5],
      [47, 4, 4, 2.4],
      [62, 3, 4, 2.2],
      [5, 19.5, 3.5, 1.9],
      [46, 19.5, 3.5, 1.8],
      [75, 19.5, 3, 1.7],
      [31, 32, 4, 1.7],
      [63, 32, 4, 1.6],
      [12, 32, 3, 1.5],
    ];
    for (const [cx, cy, rx, ry] of GRASS_BLOBS) {
      this.placeGrassBlob(cx, cy, rx, ry);
    }

    // Flower patches (near paths and building edges)
    for (let row = 0; row < 34; row++) {
      for (let col = 0; col < WORLD_COLS; col++) {
        if ((col * 11 + row * 7) % 15 !== 0) continue;
        if (isInBuilding(col, row)) continue;
        const cur = this.tileMap[row][col];
        if (cur !== TileType.Grass && cur !== TileType.DarkGrass) continue;
        // Check if adjacent to a path tile
        let nearPath = false;
        for (const [dc, dr] of [[-1,0],[1,0],[0,-1],[0,1]]) {
          const nc = col + dc;
          const nr = row + dr;
          if (nr >= 0 && nr < WORLD_ROWS && nc >= 0 && nc < WORLD_COLS) {
            if (this.tileMap[nr][nc] === TileType.Path) { nearPath = true; break; }
          }
        }
        if (nearPath || (col * 5 + row * 3) % 20 === 0) {
          this.tileMap[row][col] = TileType.FlowerPatch;
          this.collisionMap[row][col] = false;
        }
      }
    }

    // Prop collision: every world prop blocks its 1x1 anchor tile.
    for (const prop of WORLD_PROPS) {
      const { tx, ty } = prop;
      if (ty < 0 || ty >= WORLD_ROWS || tx < 0 || tx >= WORLD_COLS) continue;
      const t = this.tileMap[ty][tx];
      // Defensive: never block walk corridors even if prop data drifts.
      if (t === TileType.Path || t === TileType.Door || t === TileType.Boardwalk) continue;
      this.collisionMap[ty][tx] = true;
    }

    return {
      id: "overworld",
      name: "Miami Beach",
      type: "overworld",
      tileMap: this.tileMap,
      collisionMap: this.collisionMap,
      interactables: this.interactables,
      width: WORLD_COLS,
      height: WORLD_ROWS,
    };
  }

  /**
   * Place a department building on the map.
   */
  private placeBuilding(dept: DepartmentState, plot: BuildingPlot): void {
    // Building exterior walls
    for (let row = plot.row; row < plot.row + plot.height; row++) {
      for (let col = plot.col; col < plot.col + plot.width; col++) {
        if (
          row === plot.row ||
          row === plot.row + plot.height - 1 ||
          col === plot.col ||
          col === plot.col + plot.width - 1
        ) {
          this.tileMap[row][col] = TileType.BuildingExterior;
          this.collisionMap[row][col] = true;
        } else {
          // Interior floor (visible from outside as roof)
          this.tileMap[row][col] = TileType.BuildingFloor;
          this.collisionMap[row][col] = true; // Can't walk on building roof
        }
      }
    }

    // Door at the bottom center of the building
    const doorCol = plot.col + Math.floor(plot.width / 2);
    const doorRow = plot.row + plot.height - 1;

    this.tileMap[doorRow][doorCol] = TileType.Door;
    this.collisionMap[doorRow][doorCol] = false;
    this.tileMap[doorRow][doorCol + 1] = TileType.Door;
    this.collisionMap[doorRow][doorCol + 1] = false;

    // Make the tile in front of the door walkable
    if (doorRow + 1 < WORLD_ROWS) {
      this.collisionMap[doorRow + 1][doorCol] = false;
      this.collisionMap[doorRow + 1][doorCol + 1] = false;
    }

    // Register building interactable
    this.interactables.push({
      id: dept.id,
      type: "building",
      position: { x: doorCol * TILE_SIZE, y: doorRow * TILE_SIZE },
      width: 2 * TILE_SIZE,
      height: TILE_SIZE,
      data: {
        name: dept.name,
        type: dept.type,
        slug: dept.type,
        action: "enter",
        targetRoom: dept.id,
        plotX: plot.col * TILE_SIZE,
        plotY: plot.row * TILE_SIZE,
        plotW: plot.width * TILE_SIZE,
        plotH: plot.height * TILE_SIZE,
      },
    });

    this.buildings.set(dept.id, { ...plot, deptId: dept.id });
  }

  /**
   * Fill a rectangular region of the tile map.
   */
  private fillRect(
    col: number,
    row: number,
    width: number,
    height: number,
    tile: TileType,
    blocked: boolean,
  ): void {
    for (let r = row; r < row + height && r < WORLD_ROWS; r++) {
      for (let c = col; c < col + width && c < WORLD_COLS; c++) {
        if (r >= 0 && c >= 0) {
          this.tileMap[r][c] = tile;
          this.collisionMap[r][c] = blocked;
        }
      }
    }
  }

  /** Deterministic [0,1) noise — keeps organic terrain identical every run. */
  private noise(x: number, y: number, seed: number): number {
    let n = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 144665)) | 0;
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  }

  /** Convert plain grass inside a jitter-edged ellipse into tall (dark) grass. */
  private placeGrassBlob(cx: number, cy: number, rx: number, ry: number): void {
    const minC = Math.max(0, Math.floor(cx - rx - 1));
    const maxC = Math.min(WORLD_COLS - 1, Math.ceil(cx + rx + 1));
    const minR = Math.max(0, Math.floor(cy - ry - 1));
    const maxR = Math.min(WORLD_ROWS - 1, Math.ceil(cy + ry + 1));
    for (let r = minR; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) {
        const dx = (c - cx) / rx;
        const dy = (r - cy) / ry;
        const wobble = (this.noise(c, r, 7) - 0.5) * 0.6;
        if (dx * dx + dy * dy <= 1 + wobble && this.tileMap[r][c] === TileType.Grass) {
          this.tileMap[r][c] = TileType.DarkGrass;
          this.collisionMap[r][c] = false;
        }
      }
    }
  }

  /** Carve an organic pond (blocked water) ringed by walkable sand. */
  private placePond(cx: number, cy: number, rx: number, ry: number): void {
    const minC = Math.max(1, Math.floor(cx - rx - 1));
    const maxC = Math.min(WORLD_COLS - 2, Math.ceil(cx + rx + 1));
    const minR = Math.max(1, Math.floor(cy - ry - 1));
    const maxR = Math.min(WORLD_ROWS - 2, Math.ceil(cy + ry + 1));
    // Water body
    for (let r = minR; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) {
        const dx = (c - cx) / rx;
        const dy = (r - cy) / ry;
        const wobble = (this.noise(c, r, 13) - 0.5) * 0.4;
        if (dx * dx + dy * dy <= 1 + wobble && this.tileMap[r][c] === TileType.Grass) {
          this.tileMap[r][c] = TileType.Ocean;
          this.collisionMap[r][c] = true;
        }
      }
    }
    // Beach ring: any grass tile 8-adjacent to pond water becomes sand.
    for (let r = minR - 1; r <= maxR + 1; r++) {
      for (let c = minC - 1; c <= maxC + 1; c++) {
        if (r < 0 || r >= WORLD_ROWS || c < 0 || c >= WORLD_COLS) continue;
        if (this.tileMap[r][c] !== TileType.Grass) continue;
        let touchesWater = false;
        for (let dr = -1; dr <= 1 && !touchesWater; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr;
            const nc = c + dc;
            if (nr < 0 || nr >= WORLD_ROWS || nc < 0 || nc >= WORLD_COLS) continue;
            if (this.tileMap[nr][nc] === TileType.Ocean) {
              touchesWater = true;
              break;
            }
          }
        }
        if (touchesWater) {
          this.tileMap[r][c] = TileType.Sand;
          this.collisionMap[r][c] = false;
        }
      }
    }
  }

  /** Convert grass-type tiles in a rect to walkable path (small plaza). */
  private fillPathPlaza(col: number, row: number, width: number, height: number): void {
    for (let r = row; r < row + height; r++) {
      for (let c = col; c < col + width; c++) {
        if (r < 0 || r >= WORLD_ROWS || c < 0 || c >= WORLD_COLS) continue;
        const cur = this.tileMap[r][c];
        if (cur === TileType.Grass || cur === TileType.DarkGrass || cur === TileType.FlowerPatch) {
          this.tileMap[r][c] = TileType.Path;
          this.collisionMap[r][c] = false;
        }
      }
    }
  }

  /**
   * Get the tile type at world coordinates.
   */
  getTile(worldX: number, worldY: number): TileType {
    const col = Math.floor(worldX / TILE_SIZE);
    const row = Math.floor(worldY / TILE_SIZE);
    if (row < 0 || row >= WORLD_ROWS || col < 0 || col >= WORLD_COLS) {
      return TileType.Empty;
    }
    return this.tileMap[row][col] as TileType;
  }

  /**
   * Get whether a world position is blocked.
   */
  getCollisionAt(worldX: number, worldY: number): boolean {
    const col = Math.floor(worldX / TILE_SIZE);
    const row = Math.floor(worldY / TILE_SIZE);
    if (row < 0 || row >= WORLD_ROWS || col < 0 || col >= WORLD_COLS) {
      return true; // Out of bounds is blocked
    }
    return this.collisionMap[row][col];
  }

  /**
   * Get the department building at a world position, if any.
   */
  getBuildingAt(
    worldX: number,
    worldY: number,
  ): { deptId: string; plot: BuildingPlot } | null {
    const col = Math.floor(worldX / TILE_SIZE);
    const row = Math.floor(worldY / TILE_SIZE);

    for (const [_, building] of this.buildings) {
      if (
        col >= building.col &&
        col < building.col + building.width &&
        row >= building.row &&
        row < building.row + building.height
      ) {
        return { deptId: building.deptId, plot: building };
      }
    }
    return null;
  }
}
